/**
 * Inputs: visible card + owner write helper.
 * Outputs: a check panel for that exact card, plus a separate add form.
 * Review stays bound to the swiped card. Adding one card cannot replace the deck.
 */

import { cloneElement, isValidElement, useEffect, useRef, useState, type ReactElement, type ReactNode } from "react";
import { createId } from "@shared/ids.ts";
import { interpretPhraseSaveAck } from "@shared/phrase-save.ts";
import { missingStarterPhrases, phraseTitle } from "@shared/phrases.ts";
import type { PhraseWrite } from "@shared/phrase-write.ts";
import type { ThaiPhrase } from "@shared/schema.ts";
import type { SaveResult } from "../app/trip.tsx";
import { draftPhrase } from "../lib/api.ts";

type Candidate = {
  thai: string;
  romanization: string;
  backTranslation: string;
  pitfalls: string;
  provider?: "dictionary" | "google" | "anthropic" | "openai";
};

type CardKind = "allergy" | "dietary" | "phrase";

function providerLabel(provider?: Candidate["provider"]): string {
  if (provider === "dictionary") return "Phrase book";
  if (provider === "google") return "Google Translate";
  if (provider === "anthropic") return "Claude";
  if (provider === "openai") return "OpenAI";
  return "Draft";
}

export function PhraseOwnerTools({
  tripPhrases,
  savePhraseWrite,
  kind: defaultKind,
  visible,
  onSaved,
  section,
  deck,
}: {
  tripPhrases: ThaiPhrase[];
  savePhraseWrite: (write: PhraseWrite) => Promise<SaveResult>;
  kind: CardKind;
  visible: ThaiPhrase | null;
  onSaved?: (phrases: ThaiPhrase[], focusId?: string) => void;
  section: "dietary" | "useful";
  deck?: ReactElement<{ dock?: ReactNode }>;
}) {
  const [kind, setKind] = useState<CardKind>(defaultKind);
  const [title, setTitle] = useState("");
  const [english, setEnglish] = useState("");
  const [thai, setThai] = useState("");
  const [romanization, setRomanization] = useState("");
  const [backTranslation, setBackTranslation] = useState("");
  const [draftProvider, setDraftProvider] = useState<Candidate["provider"] | "owner">("owner");
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveNote, setSaveNote] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [draftFor, setDraftFor] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editKind, setEditKind] = useState<CardKind>(defaultKind);
  const [editTitle, setEditTitle] = useState("");
  const [editEnglish, setEditEnglish] = useState("");
  const [editThai, setEditThai] = useState("");
  const [editRoman, setEditRoman] = useState("");
  const [editBack, setEditBack] = useState("");
  const pendingCreate = useRef<ThaiPhrase | null>(null);
  const inFlight = useRef(false);
  const reviewRef = useRef<HTMLElement>(null);
  const missingStarters = missingStarterPhrases(tripPhrases);

  useEffect(() => {
    if (!saveNote || !visible) return;
    reviewRef.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [saveNote, visible?.id]);

  async function persistWrite(write: PhraseWrite, note: string, focusId?: string) {
    if (inFlight.current) return false;
    inFlight.current = true;
    setSaving(true);
    setSaveError(null);
    setSaveNote(null);
    try {
      const result = await savePhraseWrite(write);
      const ack = interpretPhraseSaveAck(result, focusId);
      if (!ack.ok) {
        setSaveError(ack.message);
        return false;
      }
      setSaveNote(note);
      onSaved?.(ack.phrases, focusId);
      return true;
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Could not save the card.");
      return false;
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }

  const editing = editingId ? tripPhrases.find((row) => row.id === editingId) ?? null : null;
  const addKindLabel = kind === "allergy" ? "allergy card" : kind === "dietary" ? "dietary card" : "phrase";

  function statusBlock() {
    if (!saveError && !saveNote) return null;
    return (
      <div className="phrase-save-status" role="status" aria-live="polite">
        {saveError ? <p className="banner crit">{saveError}</p> : null}
        {saveNote ? <p className="banner">{saveNote}</p> : null}
      </div>
    );
  }

  const checkPanel = visible ? (
    <article className="phrase-review" ref={reviewRef} data-phrase-review={section} aria-labelledby={`${section}-review-title`}>
      <header className="phrase-review-head">
        <p className="kicker">{visible.translationReviewed ? "This card" : "Checking this card"}</p>
        <p className="serif phrase-review-name" id={`${section}-review-title`}>{phraseTitle(visible)}</p>
      </header>
      <dl className="phrase-review-dl">
        <div>
          <dt>You want to say</dt>
          <dd>{visible.english}</dd>
        </div>
        <div>
          <dt>That Thai reads as</dt>
          <dd>
            {visible.backTranslation?.trim()
              ? visible.backTranslation
              : "No back-translation stored. Compare the Thai on the deck with the English above before you mark it reviewed."}
          </dd>
        </div>
      </dl>
      <div className="phrase-review-actions">
        {!visible.translationReviewed ? (
          <button
            className="btn"
            type="button"
            disabled={saving}
            onClick={() => {
              void persistWrite(
                { action: "review", id: visible.id, reviewed: true, reviewer: "owner session" },
                "Marked this card reviewed.",
                visible.id,
              );
            }}
          >
            Looks right
          </button>
        ) : (
          <button
            className="btn-ghost"
            type="button"
            disabled={saving}
            onClick={() => {
              void persistWrite(
                { action: "review", id: visible.id, reviewed: false, reviewer: "owner session" },
                "Returned this card to unreviewed.",
                visible.id,
              );
            }}
          >
            Mark this card unreviewed
          </button>
        )}
        <button
          className="btn-secondary"
          type="button"
          onClick={() => {
            setEditingId(visible.id);
            setEditKind(visible.kind ?? defaultKind);
            setEditTitle(visible.title ?? "");
            setEditEnglish(visible.english);
            setEditThai(visible.thai);
            setEditRoman(visible.romanization);
            setEditBack(visible.backTranslation ?? "");
          }}
        >
          Edit this card
        </button>
        <button
          className="btn-ghost"
          type="button"
          disabled={saving}
          onClick={() => {
            void persistWrite({ action: "remove", id: visible.id }, "Card removed.");
          }}
        >
          Remove this card
        </button>
      </div>
    </article>
  ) : (
    <p className="muted">
      {section === "useful"
        ? "No useful Thai card is selected. Add one below, or restore the starter cards."
        : "Swipe to the allergy or dietary card you want to check."}
    </p>
  );

  const deckWithDock = deck && isValidElement(deck)
    ? cloneElement(deck, { dock: checkPanel })
    : null;

  return (
    <div className={deck ? "phrase-work" : undefined} data-phrase-section={section}>
      {deckWithDock}
    <div className="phrase-tools">
      {statusBlock()}
      <section className="phrase-add">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!english.trim() || !thai.trim()) {
              setSaveError("English and Thai are needed before this card can be saved.");
              return;
            }
            const phrase: ThaiPhrase = pendingCreate.current
              ? {
                ...pendingCreate.current,
                english: english.trim(),
                thai: thai.trim(),
                romanization: romanization.trim(),
                kind,
                title: title.trim() || english.trim(),
                translationReviewed: false,
                reviewedBy: null,
                reviewedAt: null,
                backTranslation: backTranslation.trim() || undefined,
                draftProvider: draftProvider ?? "owner",
              }
              : {
                id: createId("phrase"),
                english: english.trim(),
                thai: thai.trim(),
                romanization: romanization.trim(),
                translationReviewed: false,
                kind,
                title: title.trim() || english.trim(),
                reviewedBy: null,
                reviewedAt: null,
                backTranslation: backTranslation.trim() || undefined,
                draftProvider: draftProvider ?? "owner",
              };
            pendingCreate.current = phrase;
            void persistWrite({ action: "upsert", phrase }, "Card saved. Check the Thai on this card next.", phrase.id).then((ok) => {
              if (!ok) return;
              pendingCreate.current = null;
              setTitle("");
              setEnglish("");
              setThai("");
              setRomanization("");
              setBackTranslation("");
              setDraftProvider("owner");
              setCandidates([]);
              setDraftFor(null);
            });
          }}
        >
          <h3 className="serif">Add a card</h3>
          <p className="muted">Thai stays a phrase aid until you mark this exact card reviewed. Pronunciation is optional.</p>
          <label className="field">Card type
            <select value={kind} onChange={(e) => setKind(e.target.value as CardKind)} autoComplete="off">
              <option value="phrase">Useful phrase</option>
              <option value="allergy">Allergy</option>
              <option value="dietary">Dietary</option>
            </select>
          </label>
          <label className="field">Card title
            <input value={title} onChange={(e) => setTitle(e.target.value)} autoComplete="off" />
          </label>
          <label className="field">English
            <input value={english} onChange={(e) => setEnglish(e.target.value)} required autoComplete="off" />
          </label>
          <div className="row-actions">
            <button
              className="btn-ghost"
              type="button"
              disabled={drafting || !english.trim()}
              onClick={() => {
                setDrafting(true);
                setDraftError(null);
                const intent = english.trim();
                void draftPhrase(intent, kind)
                  .then((data) => {
                    setDraftFor(data.english ?? intent);
                    setCandidates(data.candidates);
                  })
                  .catch((err: Error) => setDraftError(err.message))
                  .finally(() => setDrafting(false));
              }}
            >
              {drafting ? "Drafting…" : "Draft Thai"}
            </button>
          </div>
          {draftError ? <p className="banner warn">{draftError}</p> : null}
          {candidates.length ? (
            <div className="draft-stack">
              <p className="draft-for">Suggestions for: {draftFor ?? english}</p>
              {candidates.map((c) => (
                <article key={c.thai} className="draft-suggestion">
                  <p className="muted">{providerLabel(c.provider)}</p>
                  <p className="phrase-thai" lang="th">{c.thai}</p>
                  {c.romanization ? <p className="phrase-roman">{c.romanization}</p> : null}
                  <p><strong>That Thai reads as.</strong> {c.backTranslation}</p>
                  {c.pitfalls ? <p className="muted">{c.pitfalls}</p> : null}
                  <button
                    className="btn-secondary"
                    type="button"
                    onClick={() => {
                      setThai(c.thai);
                      setRomanization(c.romanization);
                      setBackTranslation(c.backTranslation);
                      setDraftProvider(c.provider ?? "owner");
                      if (!title.trim()) setTitle(english.trim());
                    }}
                  >
                    Use this draft
                  </button>
                </article>
              ))}
            </div>
          ) : null}
          <label className="field">Thai
            <textarea value={thai} onChange={(e) => setThai(e.target.value)} rows={2} required />
          </label>
          <label className="field">Pronunciation (optional)
            <input value={romanization} onChange={(e) => setRomanization(e.target.value)} autoComplete="off" />
          </label>
          <div className="phrase-save-row">
            <button className="btn" type="submit" disabled={saving} data-phrase-save={section}>
              {saving ? "Saving…" : `Save ${addKindLabel}`}
            </button>
          </div>
        </form>
      </section>

      {editing ? (
        <form
          className="phrase-edit"
          onSubmit={(e) => {
            e.preventDefault();
            const id = editing.id;
            void persistWrite(
              {
                action: "upsert",
                phrase: {
                  ...editing,
                  kind: editKind,
                  title: editTitle.trim() || editing.title,
                  english: editEnglish.trim(),
                  thai: editThai.trim(),
                  romanization: editRoman.trim(),
                  backTranslation: editBack.trim() || undefined,
                  draftProvider: "owner",
                  translationReviewed: false,
                  reviewedBy: null,
                  reviewedAt: null,
                },
              },
              "Edit saved as unreviewed.",
              id,
            ).then((ok) => {
              if (ok) setEditingId(null);
            });
          }}
        >
          <h3 className="serif">Edit {phraseTitle(editing)}</h3>
          <p className="muted">This edit stays on {phraseTitle(editing)}, even if you swipe the deck.</p>
          <label className="field">Card type
            <select value={editKind} onChange={(e) => setEditKind(e.target.value as CardKind)}>
              <option value="phrase">Useful phrase</option>
              <option value="allergy">Allergy</option>
              <option value="dietary">Dietary</option>
            </select>
          </label>
          <label className="field">Title<input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} /></label>
          <label className="field">English<input value={editEnglish} onChange={(e) => setEditEnglish(e.target.value)} required /></label>
          <label className="field">Thai<textarea value={editThai} onChange={(e) => setEditThai(e.target.value)} rows={2} required /></label>
          <label className="field">Pronunciation (optional)<input value={editRoman} onChange={(e) => setEditRoman(e.target.value)} /></label>
          <label className="field">That Thai reads as (optional)<input value={editBack} onChange={(e) => setEditBack(e.target.value)} /></label>
          <div className="phrase-save-row">
            <button className="btn-secondary" type="submit" disabled={saving}>Save</button>
            <button className="btn-ghost" type="button" onClick={() => setEditingId(null)}>Cancel</button>
          </div>
        </form>
      ) : null}

      {missingStarters.length > 0 && section === "useful" ? (
        <div className="phrase-restore">
          <p className="muted">{missingStarters.length} starter card{missingStarters.length === 1 ? " is" : "s are"} missing from this trip.</p>
          <button
            className="btn-secondary"
            type="button"
            disabled={saving}
            onClick={() => {
              void persistWrite({ action: "restoreStarters" }, "Starter cards restored.");
            }}
          >
            Restore starter cards
          </button>
        </div>
      ) : null}
    </div>
    </div>
  );
}
